import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  deleteField,
  doc,
  documentId,
  getDoc,
  getCountFromServer,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  limit,
  startAfter,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore'

import { getDownloadURL, ref, uploadBytes, uploadBytesResumable, deleteObject } from 'firebase/storage'
import { auth, db, functions, storage } from '../firebase'
import { getFunctions, httpsCallable } from "firebase/functions";
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { DEFAULT_INFO_MARKDOWN } from '../constants/defaultInfoMarkdown'
import { DEFAULT_EVENT_TYPES, DEFAULT_TYPE } from '../constants/eventTypes'
import { slugForType, storedWebappUrl } from '../utils/webappUrl'
import { prefillEventTemplates } from './emailTemplates'
import { titleFromFileName } from '../utils/reelTitle'
import { shareUrl } from '../utils/shareUrl'

const INFO_ALLOWED_TAGS = [
  'p', 'br', 'hr',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'strong', 'b', 'em', 'i', 'u', 'del', 's', 'ins', 'mark', 'sub', 'sup', 'small',
  'ul', 'ol', 'li',
  'a',
  'blockquote',
  'code', 'pre',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
]

const INFO_ALLOWED_ATTR = ['href', 'title', 'colspan', 'rowspan']

function renderInfoHtml(markdown) {
  const raw = marked.parse(markdown || '', { gfm: true, breaks: false })
  return DOMPurify.sanitize(raw, {
    ALLOWED_TAGS: INFO_ALLOWED_TAGS,
    ALLOWED_ATTR: INFO_ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'img', 'video', 'audio', 'svg', 'math', 'button', 'input', 'form', 'textarea', 'select', 'link', 'meta'],
    FORBID_ATTR: ['style', 'class', 'id', 'target', 'srcset', 'src'],
  })
}

//--------------------------------------------------------------------------------------------
//                              File Utils
//--------------------------------------------------------------------------------------------

async function uploadFile(path, file) {
  const storageRef = ref(storage, path)
  const snapshot = await uploadBytes(storageRef, file)
  return getDownloadURL(snapshot.ref)
}

//--------------------------------------------------------------------------------------------
//                              Users
//--------------------------------------------------------------------------------------------
export async function setAdminUserRole(uid, role, eventId) {
  await setDoc(doc(db, 'adminusers', eventId), {
    [uid]: role,
  }, { merge: true })
}

//--------------------------------------------------------------------------------------------
// Removes a user's role assignment for an event by deleting their field from the
// adminusers/{eventId} document. Does not delete the user's auth account or profile.
export async function removeAdminUserRole(uid, eventId) {
  await updateDoc(doc(db, 'adminusers', eventId), {
    [uid]: deleteField(),
  })
}

// "Forgot password". Never the SDK's sendPasswordResetEmail: that mails from
// Firebase's default sender in Firebase's copy. The callable sends our
// `passwordReset` template from the crowds.cl address, in `lang`, and returns
// to `continueUrl` (one of our apps) after the new password is set.
export async function requestPasswordReset({ email, lang, continueUrl }) {
  const call = httpsCallable(functions, 'sendPasswordReset')
  await call({ email, lang, continueUrl })
}

//--------------------------------------------------------------------------------------------
export async function createAdminUserAccount({ email, role, eventId }) {

  const inviteUser = httpsCallable(getFunctions(), "inviteUser");


  // `role` only decides the wording of the mail — the authoritative write is
  // setAdminUserRole below.
  const res = await inviteUser({ email, eventId, role });
  console.log(res.data); // { ok: true, uid, invitedEmail }

  if (!res.data.ok) {
    throw new Error('Failed to invite user')
  }


  const uid = res.data.uid

  const docRef = doc(db, 'users', uid);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists() && docSnap.data().email !== email) {
    await setDoc(docRef, {
      id: uid,
      email,
    }, { merge: true })
  }
  else {
    await setDoc(docRef, {
      id: uid,
      email,
      createdAt: serverTimestamp(),
    })
  }
  await setAdminUserRole(uid, role, eventId)

  // if (events.length) {
  //   await Promise.all(
  //     events.map((eventId) =>
  //       updateDoc(doc(db, 'events', eventId), {
  //         eventUsers: arrayUnion({
  //           userId: uid,
  //           role: accessMode === 'eventadmin' ? 'eventadmin' : 'moderator',
  //         }),
  //         memberIds: arrayUnion(uid),
  //       }),
  //     ),
  //   )
  // }

  return uid
}
// Changing or removing an account lives in src/services/userAdmin.js (the
// sysadmin's /users pages), backed by functions/lib/users.js.

// //----------------------------------------------------------------------------
// export async function fetchUsers() {
//   const snapshot = await getDocs(collection(db, 'users'))
//   return snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
// }
//--------------------------------------------------------------------------------------------
//                              Events
//--------------------------------------------------------------------------------------------

// if (eventUsers.length > 0) {
//   await Promise.all(
//     eventUsers.map((u) =>
//       updateDoc(doc(db, 'users', u.userId), {
//         events: arrayUnion(id),
//       }),
//     ),
//   )
// }

/**
 * One event type's config: the Firestore document merged on top of the
 * bundled default, so a stored document written before a key existed (e.g.
 * `videoAI`, added after every `eventTypes` doc on this installation) still
 * yields the bundled value for that key instead of nothing. A key the stored
 * document does carry always wins — this is a shallow merge, so a sysadmin
 * who deliberately configured `videoAI` on a type keeps their own object in
 * full, not merged field-by-field with the bundled one.
 * `DEFAULT_EVENT_TYPES` is already imported at the top of this file.
 */
async function fetchEventTypeConfig(typeId) {
  const id = typeId || DEFAULT_TYPE;
  const fallback = DEFAULT_EVENT_TYPES[id] || null;
  try {
    const snap = await getDoc(doc(db, 'eventTypes', id));
    if (snap.exists()) return { ...fallback, ...snap.data() };
  } catch { /* fall through to the bundled default */ }
  return fallback;
}

export async function createEvent(payload, lang) {
  const { infoMarkdown, ...eventPayload } = payload || {};
  const docRef = await addDoc(collection(db, "events"), { ...eventPayload, createdAt: serverTimestamp() });
  const md = infoMarkdown ?? DEFAULT_INFO_MARKDOWN;
  await setDoc(
    doc(db, 'events', docRef.id, 'info', 'content'),
    { markdown: md, html: renderInfoHtml(md), updatedAt: serverTimestamp() }
  );
  // The public webapp URL depends on the doc id, so it can only be written after
  // creation. Slug comes from the event's type (routing contract in the type
  // config); the id is the fresh doc id.
  const slug = slugForType(null, eventPayload.eventType);
  await setDoc(
    doc(db, 'events', docRef.id),
    { webappUrl: storedWebappUrl(slug, docRef.id) },
    { merge: true }
  );
  // Seed the event's email templates from the current defaults, in the language
  // the dashboard is being used in. Best-effort: the senders fall back to the
  // global defaults, so a failure here costs prefilled copy, not delivery.
  try {
    await prefillEventTemplates(docRef.id, lang);
  } catch (err) {
    console.warn('Could not prefill email templates:', err.message);
  }
  // Seed the event's video AI settings from its type. Best-effort, like the
  // email templates: the analysis trigger treats a missing config document as
  // disabled, so a failure here costs prefilled settings, not correctness.
  try {
    const typeConfig = await fetchEventTypeConfig(eventPayload.eventType);
    if (typeConfig?.videoAI) {
      await setDoc(
        doc(db, 'events', docRef.id, 'videoAI', 'config'),
        { ...typeConfig.videoAI, updatedAt: serverTimestamp(), updatedBy: null }
      );
    }
  } catch (err) {
    console.warn('Could not seed video AI settings:', err.message);
  }
  return docRef.id;
}
// Weddings are created server-side (functions/lib/events.js) so the sales
// app's closeSale and this dialog produce identical events.
export async function createWeddingEventCallable({ name, receptionStart, receptionEnd, lang }) {
  const call = httpsCallable(functions, 'createWeddingEvent')
  const res = await call({ name, receptionStart, receptionEnd, lang })
  return res.data.eventId
}
//----------------------------------------------------------------------------
//                              Event Types
//----------------------------------------------------------------------------
export async function fetchEventTypes() {
  const snap = await getDocs(collection(db, 'eventTypes'))
  const out = {}
  snap.forEach((d) => { out[d.id] = d.data() })
  return out
}

export async function updateEventType(typeId, config) {
  await setDoc(doc(db, 'eventTypes', typeId), config, { merge: true })
}

// Idempotent: write any type docs that don't exist yet from the bundled
// DEFAULT_EVENT_TYPES. Returns how many were created.
export async function seedEventTypes() {
  const existing = await fetchEventTypes()
  let written = 0
  for (const [typeId, config] of Object.entries(DEFAULT_EVENT_TYPES)) {
    if (!existing[typeId]) {
      await setDoc(doc(db, 'eventTypes', typeId), config)
      written += 1
    }
  }
  return written
}
//----------------------------------------------------------------------------
export async function fetchEventInfo(eventId) {
  const snap = await getDoc(doc(db, 'events', eventId, 'info', 'content'));
  return snap.exists() ? snap.data() : null;
}

export async function updateEventInfo(eventId, markdown) {
  await setDoc(
    doc(db, 'events', eventId, 'info', 'content'),
    { markdown, html: renderInfoHtml(markdown), updatedAt: serverTimestamp() },
    { merge: true }
  );
}
//----------------------------------------------------------------------------
export async function updateEvent(eventId, payload) {
  // const updates = { ...payload }
  // if (payload.eventUsers) {
  //   updates.memberIds = payload.eventUsers.map((u) => u.userId)
  // }

  await setDoc(doc(db, 'events', eventId), payload, { merge: true })
}

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}


//----------------------------------------------------------------------------
export async function deleteEvent(eventId) {
  await deleteDoc(doc(db, 'events', eventId))
  // Remove the wedding marker if present (no-op for non-wedding events).
  await deleteDoc(doc(db, 'weddings', eventId))
}

//----------------------------------------------------------------------------
export async function fetchEvents({ isSysAdmin, userEvents = null, sortBy = 'name' }) {
  // const base = collection(db, 'events')
  const ordering = sortBy === 'created' ? orderBy('createdAt', 'desc') : orderBy('name', 'asc')
  // let q = query(base, ordering)

  // // if (!includeAll && forUserId) {
  // //   q = query(base, where('memberIds', 'array-contains', forUserId), ordering)
  // // }

  // const snapshot = await getDocs(q)
  // return snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))

  const colRef = collection(db, 'events');
  if (!isSysAdmin) {
    const chunks = chunk(userEvents, 10); // if your project allows 30, bump this
    const snaps = await Promise.all(
      chunks.map((idsChunk) => {
        const q = query(colRef, where(documentId(), "in", idsChunk), ordering);
        return getDocs(q);
      })
    );
    // const flatSnaps = snaps.flat();
    // Map by id so you can preserve original order and handle missing docs
    const byId = [];
    for (const snap of snaps) {
      snap.forEach((d) => byId.push({ id: d.id, ...d.data() }));
    }
    return byId;
    // Preserve original ids order; missing docs become null
    // return userEvents.map((id) => byId.get(id) ?? null);
  }
  else {
    const q = query(colRef, ordering);
    const snapshot = await getDocs(q);
    return snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
  }
}

//----------------------------------------------------------------------------
export async function fetchEventById(eventId) {
  const snap = await getDoc(doc(db, 'events', eventId))
  return snap.exists() ? { id: snap.id, ...snap.data() } : null
}

// //----------------------------------------------------------------------------
// export async function assignUserToEvent(eventId, userId, role = 'moderator') {
//   await updateDoc(doc(db, 'events', eventId), {
//     eventUsers: arrayUnion({ userId, role }),
//     memberIds: arrayUnion(userId),
//   })
//   await updateDoc(doc(db, 'users', userId), {
//     events: arrayUnion(eventId),
//   })
// }

// //----------------------------------------------------------------------------
// export async function removeUserFromEvent(eventId, userId, role) {
//   await updateDoc(doc(db, 'events', eventId), {
//     eventUsers: arrayRemove({ userId, role }),
//     memberIds: arrayRemove(userId),
//   })
//   await updateDoc(doc(db, 'users', userId), {
//     events: arrayRemove(eventId),
//   })
// }

//----------------------------------------------------------------------------
export async function uploadEventBackground(eventId, file) {
  return uploadFile(`events/${eventId}/background`, file)
}

export async function deleteEventBackground(url) {
  const fileRef = ref(storage, url);
  try {
    await deleteObject(fileRef);
  } catch (error) {
    console.error("Error deleting event background:", error);
  }
}

//----------------------------------------------------------------------------
export async function uploadEventLogo(eventId, file) {
  return uploadFile(`events/${eventId}/logo`, file)
}

// export async function deleteEventLogo(url) {
//   const fileRef = ref(storage, url);
//   try {
//     await deleteObject(fileRef);
//   } catch (error) {
//     console.error("Error deleting event logo:", error);
//   }
// }

//----------------------------------------------------------------------------
export async function uploadEventPrizeBackground(eventId, file) {
  return uploadFile(`events/${eventId}/prize-background`, file)
}

//----------------------------------------------------------------------------
export async function uploadEventSplash(eventId, file) {
  return uploadFile(`events/${eventId}/splash`, file)
}

//--------------------------------------------------------------------------------------------
//                              Videos
//--------------------------------------------------------------------------------------------
// How many videos and reels an event holds, counted server-side (an aggregate
// query bills one read per 1000 matches, never the documents themselves).
// Each count is null when it could not be read, so one refusal hides only its own tag.
export async function fetchEventContentCounts(eventId) {
  const count = (name) => getCountFromServer(query(collection(db, name), where('eventId', '==', eventId)))
    .then((snap) => snap.data().count)
    .catch((err) => {
      console.warn(`Could not count ${name} for ${eventId}`, err)
      return null
    })
  const [videos, reels] = await Promise.all([count('videos'), count('reels')])
  return { videos, reels }
}

export async function fetchVideos(eventId, filter = 'pending') {
  const base = collection(db, 'videos')
  let constraints = [where('eventId', '==', eventId)]

  if (filter === 'pending') constraints.push(where('isModerated', '==', false))
  if (filter === 'accepted') constraints.push(where('accepted', '==', true))
  if (filter === 'rejected') constraints.push(where('accepted', '==', false))

  const q = query(base, ...constraints, orderBy('createdAt', 'asc'))
  const snapshot = await getDocs(q)
  return snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
}

export async function deleteAllEventVideos(eventId) {
  const q = query(
    collection(db, 'videos'),
    where('eventId', '==', eventId)
  )
  const snapshot = await getDocs(q)
  if (snapshot.empty) return 0

  // Delete storage files
  await Promise.all(
    snapshot.docs.map(async (docSnap) => {
      const data = docSnap.data()
      const url = data.url || data.videoUrl
      if (url) {
        try {
          await deleteObject(ref(storage, url))
        } catch (e) {
          console.warn('Failed to delete storage file for video', docSnap.id, e)
        }
      }
    })
  )

  // Delete Firestore documents in batches of 500
  const docs = snapshot.docs
  for (let i = 0; i < docs.length; i += 500) {
    const batch = writeBatch(db)
    docs.slice(i, i + 500).forEach(d => batch.delete(d.ref))
    await batch.commit()
  }

  return docs.length
}

// Delete one video everywhere: Cloudflare copy, Storage file, analysis,
// gallery item and the document. Server-side (`deleteVideo`), because the
// client reaches neither Cloudflare nor a participant's Storage object.
// Event admins and sysadmins only.
export async function deleteVideoCompletely(videoId) {
  const fn = httpsCallable(functions, 'deleteVideo')
  const res = await fn({ videoId })
  return res.data
}

// Who sent a video: `{kind: 'name'|'email'|'uid', value}`, one of them, in
// that order of preference. Server-side because staff cannot read the
// `users` profile or the Auth email.
export async function fetchVideoOwner(videoId) {
  const fn = httpsCallable(functions, 'getVideoOwner')
  const res = await fn({ videoId })
  return res.data
}

//----------------------------------------------------------------------------
export async function fetchPaginatedVideos(eventId, pageSize = 20, lastDoc = null) {
  const base = collection(db, 'videos')
  // Show all videos, not just pending/moderated ones, as per "loads all the videos for that event"
  // But maybe we want to filter? The prompt says "loads all the videos". 
  // Usually for a gallery we might want accepted ones? The user says "Make a 'videos' page... loads all the videos".
  // I will assume all videos for now, or maybe exclude rejected? 
  // PROMPT: "loads all the videos for that event" -> implies ALL. 

  let constraints = [
    where('eventId', '==', eventId),
    orderBy('createdAt', 'desc')
  ]

  if (lastDoc) {
    constraints.push(startAfter(lastDoc))
  }

  if (pageSize && pageSize !== 'all') {
    constraints.push(limit(pageSize))
  }

  const q = query(base, ...constraints)
  const snapshot = await getDocs(q)

  const videos = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data(), doc: docSnap })) // Include doc for pagination
  const lastVisible = snapshot.docs[snapshot.docs.length - 1]

  return { videos, lastVisible }
}

//----------------------------------------------------------------------------
export async function moderateVideo(videoId, accepted) {
  // Who decided matters: an AI rejection that reverses a person's call has to
  // know it did, so it can tell them. An absent `moderatedBy` means the event's
  // defaultModerated left it that way — nothing writes 'default'.
  await updateDoc(doc(db, 'videos', videoId), {
    accepted,
    isModerated: true,
    moderatedBy: 'human',
    moderatedByUid: auth.currentUser ? auth.currentUser.uid : null,
    moderatedAt: serverTimestamp(),
  })
}

//----------------------------------------------------------------------------
export async function toggleFavorite(videoId, isFavorite) {
  await updateDoc(doc(db, 'videos', videoId), { isFavorite })
}

//----------------------------------------------------------------------------
export async function updateVideoRotation(videoId, rotation) {
  await updateDoc(doc(db, 'videos', videoId), { rotation })
}

//----------------------------------------------------------------------------
export async function updateVideoForceVertical(videoId, forceVertical) {
  await updateDoc(doc(db, 'videos', videoId), { forceVertical })
}

//----------------------------------------------------------------------------
export async function toggleReelFavorite(reelId, isFavorite) {
  await updateDoc(doc(db, 'reels', reelId), { isFavorite })
}

//----------------------------------------------------------------------------
// Pull a published reel from every public surface, or put it back. Nothing is
// deleted: the reel document, its Storage object and its Cloudflare copy all
// survive, and `onReelStreamSync` rebuilds the gallery item and the share link
// from them when the switch goes back. That is the whole difference from
// `deleteReel`, and it is also why this is not the same thing as the star --
// the star says whether a reel is *featured* in the gallery, and it still
// decides that on its own terms once the reel is enabled again.
export async function toggleReelDisabled(reelId, disabled) {
  await updateDoc(doc(db, 'reels', reelId), { disabled })
}

//----------------------------------------------------------------------------
export async function bulkModerate(videoIds, accepted) {
  await Promise.all(videoIds.map((id) => moderateVideo(id, accepted)))
}

//----------------------------------------------------------------------------
export function subscribePendingVideos(eventId, callback) {
  // Every unmoderated video for the event, claimed or not. Which of them are
  // taken is a question for moderation/{eventId}/claims, read by
  // useModerationSession — deliberately not a field on the video document,
  // which its own participant can read.
  const q = query(
    collection(db, 'videos'),
    where('eventId', '==', eventId),
    where('isModerated', '==', false),
    orderBy('createdAt', 'asc')
  )
  return onSnapshot(q, (snapshot) => {
    const videos = snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
    callback(videos)
  })
}

// export async function createChallenge(name, color) {
//   return { id: randomId(), name, color }
// }

//--------------------------------------------------------------------------------------------
//                              Challenge Mode / Prizes
//--------------------------------------------------------------------------------------------

export async function generateValidatorCode(eventId) {
  const code = String(Math.floor(100000 + Math.random() * 900000)) // 6-digit
  const entry = { code, label: `Code ${code}`, createdAt: new Date().toISOString() }
  await updateDoc(doc(db, 'events', eventId), {
    validatorCodes: arrayUnion(entry),
  })
  return entry
}

export async function removeValidatorCode(eventId, codeEntry) {
  await updateDoc(doc(db, 'events', eventId), {
    validatorCodes: arrayRemove(codeEntry),
  })
}

//--------------------------------------------------------------------------------------------
//                              Reels
//--------------------------------------------------------------------------------------------

// Put a reel's bytes in Storage under a fresh, timestamped path. Replacing a
// reel's file writes a new object rather than overwriting the old one: the
// Cloudflare re-encode is triggered by `videoUrl` changing (see
// `onReelStreamSync`), and an overwrite in place is not guaranteed to mint a
// new download token.
async function putReelFile(eventId, file, onProgress) {
  const path = `reels/${eventId}/${Date.now()}-${file.name}`
  const storageRef = ref(storage, path)

  let videoUrl
  if (onProgress) {
    const uploadTask = uploadBytesResumable(storageRef, file)
    await new Promise((resolve, reject) => {
      uploadTask.on('state_changed',
        (snap) => onProgress(snap.bytesTransferred / snap.totalBytes),
        reject,
        async () => {
          videoUrl = await getDownloadURL(uploadTask.snapshot.ref)
          resolve()
        }
      )
    })
  } else {
    const snapshot = await uploadBytes(storageRef, file)
    videoUrl = await getDownloadURL(snapshot.ref)
  }

  return { path, videoUrl }
}

export async function uploadReel(eventId, file, uid, title, duration, onProgress) {
  const { path, videoUrl } = await putReelFile(eventId, file, onProgress)

  const docRef = await addDoc(collection(db, 'reels'), {
    eventId,
    title: title || titleFromFileName(file.name),
    videoUrl,
    storagePath: path,
    fileName: file.name,
    fileSize: file.size,
    duration: duration ?? null,
    uploadedBy: uid,
    // Favourited on arrival: the star is what puts a reel in the event's public
    // gallery, and uploading one is already the decision to show it. Unstarring
    // is how a reel is pulled back, not starring how it is let out.
    isFavorite: true,
    createdAt: serverTimestamp(),
  })
  return { id: docRef.id, videoUrl }
}

export async function updateReelTitle(reelId, title) {
  await updateDoc(doc(db, 'reels', reelId), { title })
}

/**
 * Set the frame Cloudflare should serve as this reel's poster, in seconds.
 *
 * No re-encode and no Cloudflare API call: the thumbnail endpoint renders any
 * frame on demand, so all that changes is the `?time=` composed into the URL
 * the gallery item and share link already store. `onReelStreamSync` rewrites
 * both in place when it sees this field move.
 *
 * `null` clears the choice and returns the reel to Cloudflare's own default
 * frame (10% in). That is NOT the same as 0, which is an explicit request for
 * the first frame — see `thumbTimeOf` in `utils/thumbnails.js`.
 */
export async function updateReelThumbTime(reelId, thumbTime) {
  await updateDoc(doc(db, 'reels', reelId), {
    thumbTime: thumbTime === null ? deleteField() : thumbTime,
  })
}

/**
 * Swap the file behind an existing reel, keeping the document.
 *
 * The point of replacing rather than deleting and re-uploading is that the reel
 * id survives, and with it the share link (`shared/r_{reelId}`), the gallery
 * item and every QR code already printed against them.
 *
 * The Cloudflare copy cannot survive: it holds the old video. `cf` is cleared
 * here so the admin UI falls back to the Storage original immediately instead
 * of playing the stale stream, and `cfDirectUrl` goes with it — without a
 * `cf.uid` to compare against, `usableCachedUrl` would hand the previous
 * rendition to the share sheet. `onReelStreamSync` sees `videoUrl` change,
 * deletes the old Cloudflare video (and the share link and gallery item that
 * point at it) and starts a fresh encode; the webhook re-mints all three under
 * the same ids when it finishes.
 */
export async function replaceReelFile(reel, file, { title, duration } = {}, onProgress) {
  const { path, videoUrl } = await putReelFile(reel.eventId, file, onProgress)

  await updateDoc(doc(db, 'reels', reel.id), {
    title: title || titleFromFileName(file.name),
    videoUrl,
    storagePath: path,
    fileName: file.name,
    fileSize: file.size,
    duration: duration ?? null,
    cf: deleteField(),
    cfDirectUrl: deleteField(),
    // The poster frame goes with them: "14 seconds in" was a statement about
    // the old video and means nothing in the new one. The fresh encode falls
    // back to Cloudflare's default frame until someone picks again.
    thumbTime: deleteField(),
  })

  // Only once the document points at the new object — an orphaned upload is
  // recoverable, a reel pointing at a deleted file is not.
  if (reel.storagePath && reel.storagePath !== path) {
    try { await deleteObject(ref(storage, reel.storagePath)) } catch { /* already gone; ignore */ }
  }

  return { videoUrl }
}

export async function deleteReel(reelId, storagePath) {
  await deleteDoc(doc(db, 'reels', reelId))
  try { await deleteObject(ref(storage, storagePath)) } catch { /* file may already be gone; ignore */ }
}

//--------------------------------------------------------------------------------------------
//                              Cloudflare Stream
//--------------------------------------------------------------------------------------------

// Re-run the Cloudflare copy for a single video or reel that failed to encode.
export async function retryStreamUpload({ kind, id }) {
  const fn = httpsCallable(functions, 'retryStreamUpload')
  const res = await fn({ kind, id })
  return res.data
}

// Finish a partial backfill for an event whose accepted videos are not all
// on Cloudflare yet. `includeErrors` also resends videos whose earlier copy
// failed, which the backfill otherwise skips (see utils/streamCopy.js).
// Event admins and sysadmins only.
export async function backfillEventStream(eventId, { includeErrors = false } = {}) {
  const fn = httpsCallable(functions, 'retryStreamUpload')
  const res = await fn({ eventId, backfill: true, includeErrors })
  return res.data
}

// Delete one video's Cloudflare copy, leaving it marked `cf.status:
// 'removed'` so it counts as missing and only the backfill sends it again.
// Sysadmin only — a tool for checking the "send to Cloudflare" button.
export async function removeStreamCopy(videoId) {
  const fn = httpsCallable(functions, 'removeStreamCopy')
  const res = await fn({ videoId })
  return res.data
}

// Fill in the creator credit on gallery items published before `authorName`
// existed. The credit comes from the source document, not from Cloudflare, so
// this repairs them in place — no re-encode.
export async function backfillGalleryAuthors(eventId) {
  const fn = httpsCallable(functions, 'backfillGalleryAuthors')
  const res = await fn({ eventId })
  return res.data
}

// Send every reel of an event that has no Cloudflare copy yet. Reels are not
// gated by the event's streaming toggle, so pre-existing reels have no
// automatic trigger — this is the catch-up path.
export async function backfillExistingReels(eventId) {
  const q = query(collection(db, 'reels'), where('eventId', '==', eventId))
  const snap = await getDocs(q)
  const pending = snap.docs.filter((d) => {
    const cf = d.data().cf
    return !cf || (!cf.uid && cf.status !== 'error')
  })
  for (const d of pending) {
    await retryStreamUpload({ kind: 'reel', id: d.id })
  }
  return { sent: pending.length }
}

//--------------------------------------------------------------------------------------------
//                              Shared Links
//--------------------------------------------------------------------------------------------

/**
 * Share links are minted by the `cloudflareStreamWebhook` function the moment a
 * reel's encode succeeds — clients cannot write to `shared` at all. The id is
 * derived from the reel id (see functions/lib/sharedLinks.js), so the URL is
 * just built here rather than looked up. Only valid once `cf.status` is
 * `ready`; before that the document does not exist yet.
 */
export function sharedReelUrl(reelId) {
  return shareUrl(`r_${reelId}`)
}

//--------------------------------------------------------------------------------------------
//                              Prizes
//--------------------------------------------------------------------------------------------

export async function fetchPrizes(eventId) {
  const q = query(
    collection(db, 'prizes'),
    where('eventId', '==', eventId),
    orderBy('createdAt', 'desc'),
  )
  const snapshot = await getDocs(q)
  return snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
}

// Ask Cloudflare to mint the MP4 rendition for a video, reel, or share link
// and report where it will live. Rendering the rendition needs the Stream API
// token, so it cannot happen in the browser. Accepts `{ shareId }` (public,
// no auth) or `{ kind, id }` (event staff). See services/videoDownload.js for
// the polling wrapper the UI actually calls.
export async function prepareStreamDownload(source) {
  const fn = httpsCallable(functions, 'prepareDownload')
  const res = await fn(source)
  return res.data
}
